
export const DAY_WIDTH = 92;
export const ROW_HEIGHT = 66;
export const LABEL_WIDTH = 210;

// Parse values as local calendar dates to prevent timezone shifts.
export const dateOnly = (value) => {
  if (!value) return new Date(NaN);

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return new Date(NaN);

    return new Date(
      value.getFullYear(),
      value.getMonth(),
      value.getDate()
    );
  }

  const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (match) {
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return new Date(NaN);
  }

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
};

// Add calendar days without changing the local calendar date unexpectedly.
export const addDays = (value, numberOfDays) => {
  const date = dateOnly(value);

  if (
    Number.isNaN(date.getTime()) ||
    !Number.isFinite(numberOfDays)
  ) {
    return new Date(NaN);
  }

  date.setDate(date.getDate() + numberOfDays);
  return date;
};

export const toISODate = (value) => {
  const date = dateOnly(value);

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// Difference between calendar dates, independent of DST.
export const daysBetween = (start, end) => {
  const a = dateOnly(start);
  const b = dateOnly(end);

  if (
    Number.isNaN(a.getTime()) ||
    Number.isNaN(b.getTime())
  ) {
    return NaN;
  }

  const aUTC = Date.UTC(
    a.getFullYear(),
    a.getMonth(),
    a.getDate()
  );

  const bUTC = Date.UTC(
    b.getFullYear(),
    b.getMonth(),
    b.getDate()
  );

  return Math.round((bUTC - aUTC) / 86400000);
};

export const formatDate = (
  value,
  options = { month: "short", day: "numeric" }
) => {
  const date = dateOnly(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString(undefined, options);
};

// Treat check-in as inclusive and check-out as exclusive.
export const overlaps = (a, b, c, d) => {
  return (
    daysBetween(a, d) > 0 &&
    daysBetween(c, b) > 0
  );
};

/**
 * Calculate the booking position within the visible timeline.
 *
 * A booking from Oct 24 to Oct 25 occupies one column:
 * left  = column for Oct 24
 * width = one DAY_WIDTH
 *
 * Bookings extending beyond the visible range are clipped.
 */



export const getBookingPosition = (stay, timelineStart, visibleDays) => {
  if (
    !stay?.checkIn ||
    !stay?.checkOut ||
    !Number.isFinite(visibleDays) ||
    visibleDays <= 0
  ) {
    return null;
  }

  const start = dateOnly(timelineStart);
  const checkIn = dateOnly(stay.checkIn);
  const checkOut = dateOnly(stay.checkOut);

  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(checkIn.getTime()) ||
    Number.isNaN(checkOut.getTime())
  ) {
    return null;
  }

  // Treat check-in and check-out dates as inclusive.
  const effectiveCheckOut =
    checkOut <= checkIn ? addDays(checkIn, 1) : addDays(checkOut, 1);

  const timelineEnd = addDays(start, Math.floor(visibleDays));

  if (effectiveCheckOut <= start || checkIn >= timelineEnd) {
    return null;
  }

  const visibleStart = checkIn < start ? start : checkIn;
  const visibleEnd =
    effectiveCheckOut > timelineEnd ? timelineEnd : effectiveCheckOut;

  const leftDays = daysBetween(start, visibleStart);
  const durationDays = daysBetween(visibleStart, visibleEnd);

  if (durationDays <= 0) {
    return null;
  }

  return {
    left: leftDays * DAY_WIDTH,
    width: durationDays * DAY_WIDTH,
  };
};




// Return consecutive local calendar dates.
export const makeDateRange = (start, numberOfDays) => {
  const firstDate = dateOnly(start);

  if (
    Number.isNaN(firstDate.getTime()) ||
    !Number.isFinite(numberOfDays) ||
    numberOfDays <= 0
  ) {
    return [];
  }

  return Array.from(
    { length: Math.floor(numberOfDays) },
    (_, index) => addDays(firstDate, index)
  );
};
